import * as C from '../common/constants';
import * as ROUTES from '../common/routes';
import CircularProgress from '@material-ui/core/CircularProgress';
import React, { useEffect, useState } from 'react';
import frameImg from '../assets/img/add_image_bg.png';
import queryString from 'query-string';
import { fetchData } from '../common/app/actions';
import { getText, getPath } from '../common/utils/helpers';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import clsx from 'clsx';
import ImageStory from './ImageStory';
import ReadMore from './ReadMore';
import SearchInput from './SearchInput';
import {
  StyleRules,
  Theme,
  createStyles,
  makeStyles,
} from '@material-ui/core/styles';

const urlPrefix = getPath();

const useStyles = makeStyles(
  (theme: Theme): StyleRules =>
    createStyles({
      searchResultContainerStyle: {
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      },
      searchInputContainerStyle: {
        width: '100%',
        maxWidth: '300px',
        marginBottom: '20px',
      },
      wrapperStyle: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      },
      centerContainerStyle: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '30vh',
      },
      searchItemStyle: {
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'row',
        marginBottom: '20px',
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: '0.7',
            transition: '0.5s ease',
          },
        },
      },
      contentContainerStyle: {
        position: 'relative',
        width: 'calc(100% - 270px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      },
      headingStyle: {
        position: 'relative',
        width: '100%',
        height: 'auto',
        fontSize: '20px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontWeight: FONT_WEIGHT.bold,
        fontFamily: FONTS.roboto,
        color: COLORS.appBrown,
      },
      dateAndPlaceStyle: {
        textAlign: 'left',
        fontSize: '14px',
        lineHeight: '20px',
        marginLeft: 'auto',
        width: '100%',
        alignSelf: 'center',
        marginBottom: '6px',
        fontFamily: FONTS.roboto,
        color: COLORS.appBrown,
      },
      textStyle: {
        fontSize: '16px',
        lineHeight: '22px',
        textAlign: 'justify',
        height: '70px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        '-webkit-line-clamp': 3,
        '-webkit-box-orient': 'vertical',
        fontFamily: FONTS.roboto,
        color: COLORS.appBrown,
      },
      imageWrapperStyle: {
        position: 'relative',
        width: '250px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        marginRight: '20px',
      },
      frameStyle: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      },
      photoStyle: {
        position: 'absolute',
        width: '90%',
        height: '86%',
        // objectFit: 'contain',
      },
      imageStyle: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
      },
      hiddenStyle: {
        display: 'none',
      },
      [`@media (max-width: 600px)`]: {
        searchItemStyle: {
          flexDirection: 'column',
        },
        contentContainerStyle: {
          width: '100%',
        },
      },
    })
);

const SearchResults = (): JSX.Element => {
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const { input } = queryString.parse(location?.search);
  const searchData = useSelector((state) => state.app.searchData);

  const [imageData, setImageData] = useState(null);

  useEffect(() => {
    dispatch(fetchData(C.SEARCH_DATA, { input }));
  }, [input]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!searchData) {
    return (
      <div className={classes.centerContainerStyle}>
        <CircularProgress />
      </div>
    );
  }

  const onClick = (event: React.SyntheticEvent, itemData) => {
    event.preventDefault();
    event.stopPropagation();

    setImageData(itemData);
  };

  const getSearchItem = (item, index) => {
    const { text, image_text, time, place, period, header } = item?.content;
    const dateAndPlace = `${time}, ${place}`;
    const imgUrl: string = `${urlPrefix}${item.images?.[0].url}`;

    return (
      <div
        className={classes.searchItemStyle}
        key={index}
        onClick={(e) => onClick(e, item)}
      >
        <div className={classes.imageWrapperStyle}>
          <img className={classes.frameStyle} src={frameImg} alt="" />
          <div className={classes.photoStyle}>
            <img
              className={classes.imageStyle}
              src={imgUrl}
              alt=""
              // onClick={(e) => openGallery(e, 0, images)}
            />
          </div>
        </div>

        <div className={classes.contentContainerStyle}>
          <div
            className={classes.headingStyle}
            dangerouslySetInnerHTML={{ __html: header }}
          />
          <div
            className={classes.dateAndPlaceStyle}
            dangerouslySetInnerHTML={{ __html: dateAndPlace }}
          />
          <div
            className={classes.textStyle}
            dangerouslySetInnerHTML={{ __html: text }}
          />
          <ReadMore />
        </div>
      </div>
    );
  };

  const onImageClose = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setImageData(null);
  };

  return (
    <div className={classes.searchResultContainerStyle}>
      <div className={classes.searchInputContainerStyle}>
        <SearchInput />
      </div>
      <div
        className={clsx(
          classes.wrapperStyle,
          Boolean(imageData) && classes.hiddenStyle
        )}
      >
        {searchData.map(getSearchItem)}
      </div>
      {Boolean(imageData) && (
        <div className={classes.wrapperStyle}>
          <ImageStory
            sliderData={[imageData]}
            currentImageNr={1}
            onClose={onImageClose}
          />
        </div>
      )}
    </div>
  );
};

export default SearchResults;
